import { reportFacebookEvent } from '@/api/public';
import { User } from '@/api/types/public';
import { getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getValidFbcValue } from '@/utils/fbcValidator';
import { getCookie } from '@/utils/tools';
import dayjs from 'dayjs';
import { sha256 } from 'js-sha256';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { resolveTrackingEventId } from '../utils/eventIdGenerator';

interface FacebookConfig {
  pixelId: string;
  isEnabled?: boolean;
}

// 购买事件允许的参数白名单
const PURCHASE_KEYS = ['value', 'currency'];

// 支持的货币类型
const SUPPORTED_CURRENCIES = ['USD', 'AUD', 'GBP', 'CAD', 'EUR'];

// Define Facebook Pixel interface
interface FbqFunction {
  (...args: any[]): void;
  callMethod?: (...args: any[]) => void;
  queue: any[];
  push: FbqFunction;
  loaded: boolean;
  version: string;
  disablePushState: boolean;
}

// Window interface已在index.d.ts中统一定义

/**
 * 从单个name字段中提取firstname和lastname
 * @param name - 完整的名字
 * @returns 返回包含firstname和lastname的对象
 */
const splitName = (name?: string): { firstname: string; lastname: string } => {
  if (!name || typeof name !== 'string') {
    return { firstname: '', lastname: '' };
  }

  const trimmedName = name.trim();
  const spaceIndex = trimmedName.indexOf(' ');

  if (spaceIndex === -1) {
    // 没有空格，整个名字作为firstname和lastname
    return {
      firstname: trimmedName,
      lastname: trimmedName,
    };
  }

  // 第一个空格前的部分作为firstname，剩余部分作为lastname
  return {
    firstname: trimmedName.substring(0, spaceIndex).trim(),
    lastname: trimmedName.substring(spaceIndex + 1).trim(),
  };
};

/**
 * 计算加密后的电话号码
 */
const getHashedPhone = (userInfo: User, needSha256 = true): string | null => {
  if (!userInfo?.telephone) {
    return null;
  }
  const phone = userInfo.tel_code
    ? `${userInfo.tel_code}${userInfo.telephone}`
    : userInfo.telephone;
  return needSha256 ? sha256(phone.replace(/[^0-9]/g, '')) : phone.replace(/[^0-9]/g, '');
};

/**
 * 转换生日格式从 YYYY-MM-DD 到 YYYYMMDD
 * @param birthday - 原始生日格式，如 "2000-01-01"
 * @returns 返回格式化后的生日 "20000101" 或 null
 */
const formatBirthday = (birthday?: string): string | null => {
  if (!birthday || typeof birthday !== 'string') {
    return null;
  }

  try {
    // 处理 YYYY-MM-DD 格式
    const parts = birthday.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      // 补零确保格式正确
      const formattedMonth = month.length === 1 ? `0${month}` : month;
      const formattedDay = day.length === 1 ? `0${day}` : day;
      return `${year}${formattedMonth}${formattedDay}`;
    }
    return null;
  } catch (error) {
    logger.error(error);
    return null;
  }
};

/**
 * 判断是否是新客户
 * @param first_order_at 首单时间，格式: "YYYY-MM-DD HH:mm:ss"
 * @param payment_time 支付时间，格式: "YYYY-MM-DD HH:mm:ss"
 * @returns 1: 新客, 2: 老客
 */
const isNewCustomer = (
  first_order_at: string | null | undefined,
  payment_time?: string,
): number => {
  // 如果首单时间不存在，认为是新客
  if (!first_order_at) {
    return 1;
  }

  // 如果没有支付时间，使用当前时间
  const paymentTimeStr = payment_time || dayjs().format('YYYY-MM-DD HH:mm:ss');

  // 解析时间
  const firstOrderTime = dayjs(first_order_at, 'YYYY-MM-DD HH:mm:ss');
  const paymentDateTime = dayjs(paymentTimeStr, 'YYYY-MM-DD HH:mm:ss');

  // 计算时间差（秒）
  const diffInSeconds = Math.abs(paymentDateTime.diff(firstOrderTime, 'second'));

  // 如果时间差在 60 秒内，认为是新客
  return diffInSeconds <= 60 ? 1 : 2;
};

/**
 * 生成加密后的用户数据
 */
const getHashedUserData = (userInfo: User) => {
  try {
    // 邮箱、电话、外部ID
    const hashedData: Record<string, string | null> = {
      em: userInfo.email ? sha256(userInfo.email.toLowerCase()) : null,
      ph: getHashedPhone(userInfo),
      external_id: userInfo.id ? sha256(String(userInfo.id)) : null,
    };

    // 处理名字
    if (userInfo.name) {
      const { firstname, lastname } = splitName(userInfo.name);
      hashedData.fn = firstname ? sha256(firstname.toLowerCase()) : null;
      hashedData.ln = lastname ? sha256(lastname.toLowerCase()) : null;
    }

    // 添加国家（加密）
    if (userInfo.vip_country_id) {
      hashedData.country = sha256(userInfo.vip_country_id.toLowerCase());
    } else if (userInfo.country_id) {
      hashedData.country = sha256(userInfo.country_id.toLowerCase());
    }

    // 添加性别（加密）
    if (userInfo.gender) {
      let gender: string | null;
      if (userInfo.gender === 'man') {
        gender = 'm';
      } else if (userInfo.gender === 'woman') {
        gender = 'f';
      } else {
        gender = null;
      }
      hashedData.ge = gender ? sha256(gender) : null;
    }

    // 添加生日（加密）
    if (userInfo.birthday) {
      const formattedBirthday = formatBirthday(userInfo.birthday);
      hashedData.db = formattedBirthday ? sha256(formattedBirthday) : null;
    }

    return hashedData;
  } catch (error) {
    logger.error(error);
    return {};
  }
};

/**
 * 为自定义数据添加用户信息 TODO 如果用户个人信息中某些信息缺失，就取默认地址中的信息
 */
const enrichCustomDataWithUserInfo = (custom: Record<string, any>, userInfo: User): void => {
  try {
    if (userInfo.email) {
      custom.customer_email = userInfo.email.toLowerCase();
      custom.em = userInfo.email.toLowerCase();
    }
    if (userInfo.id) {
      custom.customer_id = userInfo.id;
      custom.external_id = String(userInfo.id);
    }
    if (userInfo.created_at) {
      custom.created_at = userInfo.created_at;
    }

    // 处理名字
    if (userInfo.name) {
      const { firstname, lastname } = splitName(userInfo.name);
      if (firstname) custom.fn = firstname.toLowerCase();
      if (lastname) custom.ln = lastname.toLowerCase();
    }

    if (userInfo.vip_country_id) {
      custom.country = userInfo.vip_country_id.toLowerCase();
    } else if (userInfo.country_id) {
      custom.country = userInfo.country_id.toLowerCase();
    }
    if (userInfo.gender) {
      if (userInfo.gender === 'man') {
        custom.ge = 'm';
      } else if (userInfo.gender === 'woman') {
        custom.ge = 'f';
      } else {
        custom.ge = null;
      }
    }
    if (userInfo.birthday) {
      custom.db = formatBirthday(userInfo.birthday);
    }
    custom.ph = getHashedPhone(userInfo, false);
    custom.action_source = 'website';
  } catch (error) {
    logger.error(error);
  }
};

/**
 * 验证购买事件数据
 */
const validatePurchaseData = (custom: Record<string, any>): boolean => {
  try {
    const customKeys = Object.keys(custom);
    // 检查是否有缺失的必需参数
    const hasMissingKeys = PURCHASE_KEYS.some((key) => !customKeys.includes(key));
    if (hasMissingKeys) return false;
    // 要求购买事件的value不能大于999
    if (custom.value > 999) return false;
    // 要求购买事件的currency必须是支持的货币类型
    if (!SUPPORTED_CURRENCIES.includes(custom.currency)) return false;
    return true;
  } catch (error) {
    logger.error(error);
    return false;
  }
};

export class FacebookProvider implements Provider {
  type = ProviderType.FACEBOOK;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: FacebookConfig;

  constructor(config: FacebookConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Facebook Pixel using the original script logic
      await this.initializeFacebookPixel();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Facebook Pixel:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Facebook tracking event:', JSON.stringify(event));
      }

      // 使用从 TrackingEvent 中传入的 eventId，如果没有则走统一事件 ID 生成器。
      const eventId = resolveTrackingEventId(event.eventId);
      // 获取当前的 store 状态
      const storeState = getStoreState();
      const { userInfo } = storeState;
      const customData = event.properties || {};

      // 确保 content_ids 为数组格式
      if (customData.content_ids && !Array.isArray(customData.content_ids)) {
        customData.content_ids = [customData.content_ids];
      }

      // 为所有事件添加用户信息
      enrichCustomDataWithUserInfo(customData, userInfo);

      // 添加新老客户标识（所有事件统一处理）
      if (userInfo.first_order_at) {
        customData.is_new_customer = 2;
        customData.first_order_at = userInfo.first_order_at;
        customData.min = userInfo.first_order_at;
      } else {
        customData.is_new_customer = 1;
      }
      // 如果是购买事件，根据支付时间计算新老客户标识
      if (event.eventName === 'Purchase') {
        const paymentTime = customData.payment_time; // Purchase 事件会传入 payment_time
        customData.is_new_customer = isNewCustomer(userInfo.first_order_at, paymentTime);
      }

      // 如果是购买事件，验证数据是否符合要求
      if (event.eventName === 'Purchase' && !validatePurchaseData(customData)) {
        logger.error(
          'Purchase fbq event not sent-- 购买事件数据不符合条件，不发送事件',
          JSON.stringify(customData),
        );
        return;
      }

      // 提取名字信息
      const { firstname, lastname } = userInfo.name
        ? splitName(userInfo.name)
        : { firstname: '', lastname: '' };

      // 准备服务器端事件数据
      // 注：后端会增加字段:
      // event_time
      // client_ip_address
      // client_user_agent
      // action_source
      // 前端重复字段先留着。
      const tempObj = {
        event_name: event.eventName,
        event_id: eventId,
        event_source_url: location.href,
        referrer_url: document.referrer || null,
        client_user_agent: navigator.userAgent,
        user_data: {
          // 明文用户数据
          email: userInfo.email || null,
          firstname,
          lastname,
          // 加密版用户信息
          ...getHashedUserData(userInfo),
          // Facebook 跟踪参数
          fbp: getCookie('_fbp') || null,
          fbc: getValidFbcValue() || null,
        },
        custom_data: customData,
        action_source: 'website', // pc或m传website，ios或android传app
      };

      if (this.loggingEnabled) {
        logger.log(
          'facebook event sent-- 最终发送事件',
          event.eventName,
          JSON.stringify(customData),
        );
      }
      window.fbq(event.commandType || 'track', event.eventName, customData, { eventID: eventId });
      reportFacebookEvent(tempObj);
    } catch (error) {
      logger.error('Facebook Pixel tracking error:', error);
    }
  }

  private initializeFacebookPixel(): Promise<void> {
    return new Promise((resolve, reject) => {
      const { pixelId } = this.config;

      // Initialize fbq function if it doesn't exist
      if (!window.fbq) {
        const f = window;
        f.fbq = function () {
          if (n.callMethod) {
            // eslint-disable-next-line prefer-spread, prefer-rest-params
            n.callMethod.apply(n, arguments);
          } else {
            // eslint-disable-next-line prefer-rest-params
            n.queue.push(arguments);
          }
        } as FbqFunction;
        const n = f.fbq;

        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
      }

      // Load Facebook script using ga4Provider style
      const firstScript = document.getElementsByTagName('script')[0];
      const fbScript = document.createElement('script');
      fbScript.async = true;
      fbScript.src = 'https://connect.facebook.net/en_US/fbevents.js';

      // Handle script load event
      fbScript.onload = () => {
        try {
          // Configure fbq settings
          window.fbq.disablePushState = true; // 禁止fbq在路由更改之后自动发送PageView事件

          // Initialize with pixel ID
          const ids = pixelId.split(',');
          window.fbq('init', ids[0]);

          if (ids[1]) {
            window.fbq('init', ids[1]);
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      };

      // Handle script error event
      fbScript.onerror = () => {
        reject(new Error('Failed to load Facebook Pixel script'));
      };

      firstScript.parentNode.insertBefore(fbScript, firstScript);
    });
  }

  /**
   * 启用Facebook日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Facebook日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
